import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getHandbook, trackHandbookProgress, askHandbookQuestion, emailHandbookSection, GetHandbookOutputType } from 'zite-endpoints-sdk';
import MiniQuiz, { PreviousAttempt } from '../components/handbook/MiniQuiz';
import FreeTextQuiz from '../components/handbook/FreeTextQuiz';
import EpicPubGuide from '../components/handbook/EpicPubGuide';
import AgeEstimationQuiz from '../components/handbook/AgeEstimationQuiz';
import GlassGuide from '../components/handbook/GlassGuide';
import FireSafetyInfo from '../components/handbook/FireSafetyInfo';
import FoodHygieneGuide from '../components/handbook/FoodHygieneGuide';
import ServiceExamples from '../components/handbook/ServiceExamples';
import PointsGuide from '../components/handbook/PointsGuide';
import AddNoteDialog from '../components/journal/AddNoteDialog';
import { useFeatureToggles } from '../components/FeatureToggleProvider';

function isSafeAndLegal(title: string) {
  const t = title.toLowerCase();
  return (t.includes('safe') && t.includes('legal')) || t.includes('challenge 25') || t.includes('licensing');
}
function isAgeEstimation(title: string, body: string) {
  const t = title.toLowerCase();
  const hasImages = /!\[.*?\]\(https?:\/\/[^)]+\)/.test(body);
  return hasImages && (t.includes('age') || t.includes('how old') || t.includes('estimat') || t.includes('guess'));
}
function needsGlassGuide(title: string) {
  const t = title.toLowerCase();
  return t.includes('glass') || t.includes('measure') || t.includes('pour') || t.includes('draught');
}
function needsFireSafety(title: string) {
  const t = title.toLowerCase();
  return t.includes('fire') || t.includes('emergency') || t.includes('evacuation');
}
function needsFoodHygiene(title: string) {
  const t = title.toLowerCase();
  return t.includes('food') || t.includes('hygiene') || t.includes('allerg');
}
function isEpicPub(title: string) {
  const t = title.toLowerCase();
  return t.includes('epic pub') || t.includes('product knowledge') || (t.includes('house') && t.includes('style'));
}
function needsServiceExamples(title: string) {
  const t = title.toLowerCase();
  return !isEpicPub(title) && (t.includes('upsell') || t.includes('service') || t.includes('customer journey') || t.includes('recovery'));
}
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { BookOpen, CheckCircle, MessageCircle, Send, Loader2, ChevronRight, Download, Mail, StickyNote, Brain } from 'lucide-react';
import { toast } from 'sonner';
import { Markdown } from '@/components/markdown';
import { cn } from '@/lib/utils';

type Section = GetHandbookOutputType['sections'][0];
type QuizAttemptSummary = GetHandbookOutputType['quizAttempts'][0];

const LOGO_URL = 'https://staffhub.wcdns.co.uk/rattlebone-logo.png';

function buildPrintHtml(section: Section, contentHtml: string) {
  const year = new Date().getFullYear();
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${section.title} — Rattlebone Staff Handbook</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; color: #1a1a1a; background: #fff; line-height: 1.6; }
    .page { max-width: 760px; margin: 0 auto; padding: 48px 40px; }
    .header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 20px; border-bottom: 3px solid #A36B58; margin-bottom: 36px; }
    .logo { height: 52px; width: auto; }
    .header-label { text-align: right; font-size: 13px; color: #888; letter-spacing: 0.04em; font-weight: 500; text-transform: uppercase; }
    h1.section-title { font-size: 28px; font-weight: 700; color: #1a1a1a; margin-bottom: 28px; padding-bottom: 12px; border-bottom: 1px solid #eee; }
    .content h1 { font-size: 22px; font-weight: 700; margin: 24px 0 12px; color: #1a1a1a; }
    .content h2 { font-size: 18px; font-weight: 600; margin: 20px 0 10px; color: #222; }
    .content h3 { font-size: 15px; font-weight: 600; margin: 16px 0 8px; color: #333; }
    .content p { margin-bottom: 14px; color: #333; }
    .content img { max-width: 100%; height: auto; border-radius: 6px; margin: 20px 0; display: block; box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .content ul, .content ol { margin: 8px 0 16px 24px; }
    .content li { margin-bottom: 6px; color: #333; }
    .content table { width: 100%; border-collapse: collapse; margin: 16px 0; font-size: 14px; }
    .content td, .content th { border: 1px solid #ddd; padding: 10px 14px; text-align: left; }
    .content th { background: #faf6f4; font-weight: 600; color: #A36B58; }
    .content tr:nth-child(even) { background: #fafafa; }
    .content blockquote { border-left: 4px solid #A36B58; padding: 8px 16px; margin: 16px 0; background: #faf6f4; border-radius: 0 4px 4px 0; }
    .content strong { font-weight: 600; color: #1a1a1a; }
    .content code { background: #f5f5f5; padding: 2px 6px; border-radius: 3px; font-family: monospace; font-size: 0.88em; }
    .footer { margin-top: 64px; padding-top: 20px; border-top: 1px solid #ddd; display: flex; align-items: center; justify-content: space-between; }
    .footer-logo { height: 28px; opacity: 0.5; }
    .footer-text { font-size: 11px; color: #aaa; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { margin: 18mm 20mm; }
      .page { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <img class="logo" src="${LOGO_URL}" alt="The Rattlebone" />
      <span class="header-label">Staff Handbook</span>
    </div>
    <h1 class="section-title">${section.sectionOrder}. ${section.title}</h1>
    <div class="content">${contentHtml}</div>
    <div class="footer">
      <img class="footer-logo" src="${LOGO_URL}" alt="Rattlebone" />
      <span class="footer-text">© Rattlebone Sherston Ltd ${year}. All rights reserved. Confidential — for staff use only.</span>
    </div>
  </div>
</body>
</html>`;
}

export default function Handbook() {
  const { user } = useAuth();
  const { isEnabled } = useFeatureToggles();
  const quizzesEnabled = isEnabled('handbook-quizzes');
  const [sections, setSections] = useState<Section[]>([]);
  const [openedIds, setOpenedIds] = useState<string[]>([]);
  const [quizAttempts, setQuizAttempts] = useState<QuizAttemptSummary[]>([]);
  const [selected, setSelected] = useState<Section | null>(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [qa, setQa] = useState<{ q: string; a: string }[]>([]);
  const [asking, setAsking] = useState(false);
  const [streamedAnswer, setStreamedAnswer] = useState('');
  const qaRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Email dialog
  const [emailOpen, setEmailOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [sending, setSending] = useState(false);

  const loadData = useCallback(() => {
    if (!user) return;
    getHandbook({})
      .then(d => {
        setSections(d.sections);
        setOpenedIds(d.openedSectionIds);
        setQuizAttempts(d.quizAttempts);
        if (d.sections.length > 0) selectSection(d.sections[0], d.openedSectionIds);
      })
      .catch(() => toast.error('Failed to load handbook'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const selectSection = async (section: Section, currentOpened?: string[]) => {
    setSelected(section);
    setQa([]);
    setStreamedAnswer('');
    const opened = currentOpened ?? openedIds;
    if (!opened.includes(section.id)) {
      setOpenedIds(prev => [...prev, section.id]);
      trackHandbookProgress({ sectionId: section.id }).catch(() => {});
    }
  };

  // Refresh quiz attempts after a quiz is completed
  const handleQuizComplete = () => {
    getHandbook({}).then(d => {
      setQuizAttempts(d.quizAttempts);
    }).catch(() => {});
  };

  const askQuestion = async () => {
    if (!question.trim() || !selected) return;
    const q = question.trim();
    setQuestion('');
    setAsking(true);
    setStreamedAnswer('');
    let answer = '';
    try {
      const stream = askHandbookQuestion({ question: q, sectionTitle: selected.title, sectionBody: selected.body });
      for await (const chunk of stream) {
        answer += chunk;
        setStreamedAnswer(answer);
      }
      setQa(prev => [...prev, { q, a: answer }]);
      setStreamedAnswer('');
    } catch {
      toast.error('Failed to get answer');
    } finally {
      setAsking(false);
    }
    setTimeout(() => qaRef.current?.scrollTo({ top: qaRef.current.scrollHeight, behavior: 'smooth' }), 50);
  };

  const handleDownloadPdf = () => {
    if (!selected || !contentRef.current) { toast.error('No section selected'); return; }
    const contentHtml = contentRef.current.innerHTML;
    const printWin = window.open('', '_blank', 'width=900,height=750');
    if (!printWin) { toast.error('Please allow pop-ups to download as PDF'); return; }
    printWin.document.write(buildPrintHtml(selected, contentHtml));
    printWin.document.close();
    setTimeout(() => { printWin.focus(); printWin.print(); }, 900);
  };

  const handleSendEmail = async () => {
    if (!selected || !recipientEmail.trim()) return;
    setSending(true);
    try {
      await emailHandbookSection({
        recipientEmail: recipientEmail.trim(),
        sectionTitle: selected.title,
        sectionOrder: selected.sectionOrder,
        sectionBody: selected.body,
      });
      toast.success(`Email sent to ${recipientEmail.trim()}`);
      setEmailOpen(false);
      setRecipientEmail('');
    } catch { toast.error('Failed to send email'); }
    finally { setSending(false); }
  };

  const getQuizAttempt = (sectionId: string): PreviousAttempt | undefined => {
    const a = quizAttempts.find(q => q.sectionId === sectionId);
    if (!a) return undefined;
    return {
      lastAttemptId: a.lastAttemptId,
      lastScore: a.lastScore,
      lastTotal: a.lastTotal,
      lastPercentage: a.lastPercentage,
      lastPointsEarned: a.lastPointsEarned,
      bestPercentage: a.bestPercentage,
      totalAttempts: a.totalAttempts,
    };
  };

  if (loading) {
    return (
      <div className="px-6 py-8 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-6">
          <Skeleton className="w-48 h-96" />
          <Skeleton className="flex-1 h-96" />
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <BookOpen className="w-12 h-12 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold text-foreground mb-2">Handbook Not Available</h2>
        <p className="text-muted-foreground text-sm">The staff handbook hasn't been published yet. Check back soon.</p>
      </div>
    );
  }

  const progress = sections.length > 0 ? Math.round((openedIds.length / sections.length) * 100) : 0;

  return (
    <div className="flex flex-col h-[calc(100vh-3.5rem)]">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-xl font-bold text-foreground">Staff Handbook</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{quizzesEnabled ? 'Read through each section. Take the quiz to earn points!' : 'Read through each section to complete your training.'}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground mb-1">{openedIds.length} / {sections.length} sections read</p>
          <div className="w-32 bg-muted rounded-full h-1.5">
            <div className="bg-primary h-full rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <div className="w-60 shrink-0 border-r border-border bg-card">
          <ScrollArea className="h-full">
            <div className="p-3 space-y-0.5">
              <p className="text-xs text-muted-foreground uppercase tracking-wider px-2 py-2">Sections</p>
              {sections.map(s => {
                const attempt = getQuizAttempt(s.id);
                const isRead = openedIds.includes(s.id);
                const isSelected = selected?.id === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => selectSection(s)}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors group',
                      isSelected
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5 shrink-0">
                        {isRead ? (
                          <CheckCircle className="w-3.5 h-3.5 text-primary" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                      </div>
                      <span className={cn('leading-snug flex-1 min-w-0 truncate', isSelected && 'font-medium')}>
                        {s.sectionOrder}. {s.title}
                      </span>
                    </div>
                    {/* Quiz status row — only if handbook-quizzes toggle is ON */}
                    {quizzesEnabled && attempt ? (
                      <div className="ml-5.5 mt-1 flex items-center gap-1">
                        <Brain className="w-3 h-3 text-primary shrink-0" />
                        <span className={cn(
                          'text-xs font-medium',
                          attempt.bestPercentage >= 80 ? 'text-green-600' :
                          attempt.bestPercentage >= 50 ? 'text-yellow-600' : 'text-red-500'
                        )}>
                          {attempt.bestPercentage}% best
                        </span>
                      </div>
                    ) : quizzesEnabled && isRead ? (
                      <div className="ml-5.5 mt-1 flex items-center gap-1">
                        <Brain className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground">Quiz available</span>
                      </div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          {selected ? (
            <>
              <ScrollArea className="flex-1">
                <div className="p-6">
                  <div className="border border-border rounded-lg bg-card p-6 mb-4">
                    {/* Section header + action buttons */}
                    <div className="flex items-start justify-between gap-4 mb-5">
                      <h2 className="text-xl font-semibold text-foreground">{selected.sectionOrder}. {selected.title}</h2>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 h-8 text-xs"
                          onClick={() => setNoteOpen(true)}
                        >
                          <StickyNote className="w-3.5 h-3.5" />
                          Add Note
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 h-8 text-xs"
                          onClick={() => setEmailOpen(true)}
                        >
                          <Mail className="w-3.5 h-3.5" />
                          Email page
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 h-8 text-xs"
                          onClick={handleDownloadPdf}
                        >
                          <Download className="w-3.5 h-3.5" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                    <div className="prose prose-sm max-w-none text-foreground" ref={contentRef}>
                      <Markdown>{selected.body}</Markdown>
                    </div>
                  </div>

                  {/* Epic Pub interactive guide */}
                  {selected && isEpicPub(selected.title) && <EpicPubGuide />}

                  {/* Contextual visual infographics */}
                  {selected && needsGlassGuide(selected.title) && <GlassGuide />}
                  {selected && needsFireSafety(selected.title) && <FireSafetyInfo />}
                  {selected && needsFoodHygiene(selected.title) && <FoodHygieneGuide />}
                  {selected && needsServiceExamples(selected.title) && <ServiceExamples />}
                  {selected && (selected.title.toLowerCase().includes('training') || selected.title.toLowerCase().includes('induction') || selected.title.toLowerCase().includes('introduction') || selected.title.toLowerCase().includes('reward') || selected.title.toLowerCase().includes('point')) && <PointsGuide />}

                  {/* Quiz — per-section, gated behind handbook-quizzes toggle */}
                  {quizzesEnabled && selected && isSafeAndLegal(selected.title) ? (
                    <FreeTextQuiz sectionId={selected.id} sectionTitle={selected.title} sectionBody={selected.body} />
                  ) : quizzesEnabled && selected && isAgeEstimation(selected.title, selected.body) ? (
                    <AgeEstimationQuiz sectionId={selected.id} sectionTitle={selected.title} sectionBody={selected.body} />
                  ) : quizzesEnabled && selected ? (
                    <MiniQuiz
                      key={selected.id}
                      sectionId={selected.id}
                      sectionTitle={selected.title}
                      sectionBody={selected.body}
                      previousAttempt={getQuizAttempt(selected.id)}
                      onAttemptComplete={handleQuizComplete}
                    />
                  ) : null}

                  {/* Q&A area */}
                  <div className="border border-border rounded-lg bg-card p-4 mt-4">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wider flex items-center gap-1.5 mb-3">
                      <MessageCircle className="w-3.5 h-3.5 text-primary" /> Ask about this section
                    </p>
                    <div ref={qaRef} className="space-y-3 max-h-64 overflow-y-auto mb-3">
                      {qa.map((item, i) => (
                        <div key={i}>
                          <div className="bg-muted/50 rounded-md px-3 py-2 text-sm mb-1">
                            <p className="text-xs text-muted-foreground font-medium mb-1">You asked:</p>
                            <p className="text-foreground">{item.q}</p>
                          </div>
                          <div className="bg-primary/5 border border-primary/15 rounded-md px-3 py-2 text-sm">
                            <p className="text-xs text-primary font-medium mb-1">Answer:</p>
                            <Markdown>{item.a}</Markdown>
                          </div>
                        </div>
                      ))}
                      {(asking || streamedAnswer) && (
                        <div className="bg-primary/5 border border-primary/15 rounded-md px-3 py-2 text-sm">
                          <p className="text-xs text-primary font-medium mb-1">Answer:</p>
                          {streamedAnswer ? <Markdown>{streamedAnswer}</Markdown> : (
                            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Thinking...</div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        value={question}
                        onChange={e => setQuestion(e.target.value)}
                        placeholder="Type your question..."
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && askQuestion()}
                        disabled={asking}
                        className="flex-1"
                      />
                      <Button onClick={askQuestion} disabled={asking || !question.trim()} className="shrink-0">
                        {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </ScrollArea>

              {/* Navigation */}
              <div className="border-t border-border px-6 py-3 flex justify-between items-center shrink-0 bg-card">
                <Button
                  variant="outline" size="sm"
                  onClick={() => { const idx = sections.findIndex(s => s.id === selected.id); if (idx > 0) selectSection(sections[idx - 1]); }}
                  disabled={sections.findIndex(s => s.id === selected.id) === 0}
                >
                  ← Previous
                </Button>
                <span className="text-xs text-muted-foreground">
                  {sections.findIndex(s => s.id === selected.id) + 1} of {sections.length}
                </span>
                <Button
                  size="sm"
                  onClick={() => { const idx = sections.findIndex(s => s.id === selected.id); if (idx < sections.length - 1) selectSection(sections[idx + 1]); }}
                  disabled={sections.findIndex(s => s.id === selected.id) === sections.length - 1}
                >
                  Next →
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              Select a section to begin
            </div>
          )}
        </div>
      </div>

      {/* Add Note Dialog */}
      {selected && (
        <AddNoteDialog
          open={noteOpen}
          onOpenChange={setNoteOpen}
          sourcePage={`Handbook: ${selected.title}`}
          sourcePath="/handbook"
          defaultTitle={`Note on: ${selected.title}`}
        />
      )}

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={v => { if (!sending) { setEmailOpen(v); if (!v) setRecipientEmail(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" /> Email this page as PDF
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="border border-border rounded-lg bg-muted/30 p-3">
              <p className="text-xs text-muted-foreground mb-0.5">Section</p>
              <p className="text-sm font-medium text-foreground">{selected?.sectionOrder}. {selected?.title}</p>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Recipient email address</label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                placeholder="name@example.com"
                onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
                autoFocus
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The recipient will receive a beautifully formatted email with the Rattlebone branding, section content, and all images.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setEmailOpen(false); setRecipientEmail(''); }} disabled={sending}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleSendEmail} disabled={sending || !recipientEmail.trim()}>
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4 mr-2" /> Send Email</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
